import Chip from '@mui/material/Chip';
import { styled } from '@mui/material/styles';
import { format } from 'date-fns/format';
import { fr } from 'date-fns/locale/fr';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { capitalizeFirst } from '@bloomays-lib/utils.shared';
import { dsV2, SingleLineText } from '@bloomays-lib/ui.shared';
import {
  buildPurchaseOrderDatePresets,
  matchesPurchaseOrderDatePreset,
  PurchaseOrderDatePreset,
} from '../../helpers/purchaseOrderDatePresets';

export type PurchaseOrderDatePresetsProps = {
  startDate: Date | null;
  endDate: Date | null;
  onSelect: (startDate: Date, endDate: Date) => void;
  /**
   * Date de référence utilisée pour construire les périodes proposées.
   * Vaut l'heure courante par défaut ; à surcharger uniquement pour un rendu
   * déterministe (tests, snapshots Storybook/Chromatic).
   */
  now?: Date;
};

/**
 * Chips de préremplissage de la période d'un bon de commande (mois / trimestre /
 * semestre autour de la date du jour), pour éviter de composer les dates à la main.
 */
export const PurchaseOrderDatePresets = ({ startDate, endDate, onSelect, now }: PurchaseOrderDatePresetsProps) => {
  const { t } = useTranslation(['purchaseOrderDashboard']);
  const referenceNow = now ?? new Date();
  const presets = useMemo(() => buildPurchaseOrderDatePresets(referenceNow), [referenceNow]);

  const presetLabel = (preset: PurchaseOrderDatePreset): string => {
    if (preset.kind === 'quarter') {
      return t('formDatePresetQuarter', { number: preset.periodNumber, year: preset.year });
    }
    if (preset.kind === 'semester') {
      return t('formDatePresetSemester', { number: preset.periodNumber, year: preset.year });
    }
    return capitalizeFirst(format(preset.startDate, 'LLL yyyy', { locale: fr }));
  };

  const presetRange = (preset: PurchaseOrderDatePreset): string =>
    `${format(preset.startDate, 'dd/MM/yyyy')} → ${format(preset.endDate, 'dd/MM/yyyy')}`;

  return (
    <PresetsContainer>
      <PresetsLabel text={t('formDatePresetsLabel')} variant="caption" />
      <PresetsRow role="group" aria-label={t('formDatePresetsLabel')}>
        {presets.map((preset) => {
          const selected = matchesPurchaseOrderDatePreset(preset, startDate, endDate);
          return (
            <PresetChip
              key={preset.id}
              label={presetLabel(preset)}
              title={presetRange(preset)}
              aria-pressed={selected}
              selected={selected}
              clickable
              onClick={() => onSelect(preset.startDate, preset.endDate)}
            />
          );
        })}
      </PresetsRow>
    </PresetsContainer>
  );
};

export default PurchaseOrderDatePresets;

const PresetsContainer = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
}));

const PresetsLabel = styled(SingleLineText)(() => ({
  color: dsV2.color.t3,
  textAlign: 'left',
}));

const PresetsRow = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  gap: 6,
}));

const PresetChip = styled(Chip, { shouldForwardProp: (prop) => prop !== 'selected' })<{ selected: boolean }>(
  ({ selected }) => ({
    height: 'auto',
    borderRadius: dsV2.radius.pill,
    border: `1px solid ${selected ? dsV2.color.bleuBrd : dsV2.color.border}`,
    backgroundColor: selected ? dsV2.color.bleuDoux : dsV2.color.surface2,
    color: selected ? dsV2.color.bleuDark : dsV2.color.t3,
    '& .MuiChip-label': {
      padding: '3px 10px',
      fontFamily: dsV2.font.sansSemiBold,
      fontWeight: 600,
      fontSize: 11,
      lineHeight: 1.45,
      color: 'inherit',
      whiteSpace: 'nowrap',
    },
    '&:hover, &:focus': {
      backgroundColor: selected ? dsV2.color.bleuDoux : dsV2.color.bleuDouxLight,
      color: dsV2.color.bleuDark,
    },
  }),
);
